import React, {FC, useState, useEffect} from "react"
import moment from "moment"

import FormattedMessage, {getMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {H2, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import Button from "@irati/ui/src/Button"
import {Radio, CustomNodeRenderer} from "@irati/ui/src/FormItem"
import theme from "@irati/ui/src/global/theme"
import adminTracking from "@irati-tracking/admin"
import workosService from "@irati/workos/services/workos"
import authentication from "@irati/auth"
import ErrorText from "@irati/ui/src/ErrorText"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"

import AbandonModal from "./AbandonModal"

export enum LaunchTime {
  AfterSetup = "after_setup",
  DelayUntilReady = "delay_until_ready"
}

const LAUNCH_TIME_OPTIONS = [
  {
    customNode: (
      <Body color={theme.colors.utility.ink} marginTop={theme.spacing.half} marginBottom={theme.spacing.half}>
        <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.launchEmails.afterSetup" />
      </Body>
    ),
    value: LaunchTime.AfterSetup,
    whiteSpace: "wrap"
  },
  {
    customNode: (
      <Body color={theme.colors.utility.ink} marginTop={theme.spacing.half} marginBottom={theme.spacing.half}>
        <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.launchEmails.delay" />
      </Body>
    ),
    value: LaunchTime.DelayUntilReady,
    whiteSpace: "wrap"
  }
]

interface Props extends InjectIntl {
  show: boolean
  onClose: () => void
  onNext: (launchTime: LaunchTime) => void
}

const LaunchInvitationsModal: FC<Props> = ({show, onClose, onNext, formatMessage}) => {
  const [launchTime, setLaunchTime] = useState<LaunchTime>()
  const [previousLaunchTime, setPreviousLaunchTime] = useState<LaunchTime>()
  const [submitLoad, setSubmitLoad] = useState<Promise<void>>()
  const [error, setError] = useState<string>()
  const [showModal, setShowModal] = useState(show)
  const [showAbandonModal, setShowAbandonModal] = useState(false)

  const handleOnNext = () => {
    setError(undefined)
    const date = launchTime === LaunchTime.AfterSetup ? moment().unix().toString() : moment().add(10, "year").unix().toString()
    const load = workosService
      .setDsyncConfig(authentication.business().uuid, {seconds: date})
      .then(() => {
        adminTracking.hrisUpsellSetup("admin_dysnc_set_up_invite_email_options", {
          action: launchTime === LaunchTime.AfterSetup ? "email_users_immediately" : "delay_email_to_users"
        })
        onClose()
        onNext(launchTime!)
        setLaunchTime(undefined)
      })
      .catch((err: PBError) => {
        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        setError(message)
      })
    setSubmitLoad(load)
  }

  const getDsyncConfig = () => {
    workosService.getDsyncConfig(authentication.business().uuid).then(({directorySyncConfig}) => {
      if (directorySyncConfig?.launchAt?.seconds) {
        const date = moment.unix(directorySyncConfig?.launchAt.seconds)
        setPreviousLaunchTime(date.isSameOrBefore() ? LaunchTime.AfterSetup : LaunchTime.DelayUntilReady)
      }
    })
  }

  const handleOnReturn = () => {
    setShowAbandonModal(false)
    setShowModal(true)
  }

  const handleOnClose = () => {
    setShowAbandonModal(false)
    onClose()
    setLaunchTime(undefined)
  }

  const handleOnHide = () => {
    setShowModal(false)
    setShowAbandonModal(true)
  }

  useEffect(() => {
    setShowModal(show)
    getDsyncConfig()
  }, [show])

  const infoTitle =
    previousLaunchTime === LaunchTime.AfterSetup
      ? "admin.components.integration.directorySync.hris.upsell.launchEmails.info.afterSetup"
      : "admin.components.integration.directorySync.hris.upsell.launchEmails.info.delayed"

  return (
    <>
      <GenericModal customWidth={700} hideActions show={showModal} onHide={handleOnHide}>
        <H2 marginBottom={2}>
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.launchEmails.title" />
        </H2>

        {previousLaunchTime && <Alert type={AlertTypes.info} title={infoTitle} />}

        <Body marginBottom={2}>
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.launchEmails.subTitle" />
        </Body>
        <Body color={theme.colors.utility.ink} large marginBottom={theme.spacing.half}>
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.launchEmails.label" />
        </Body>
        <Radio
          rowRenderer={CustomNodeRenderer}
          name="launch_time"
          value={launchTime}
          onChange={(value) => setLaunchTime(value)}
          options={LAUNCH_TIME_OPTIONS}
        />
        <Body small color={theme.colors.utility.ink} marginTop marginBottom>
          <em>
            <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.launchEmails.hint" />
          </em>
        </Body>
        <Flex justifyContent="flex-end">
          <Button load={submitLoad} primary disabled={!launchTime} onClick={handleOnNext} paddingLeft={10} paddingRight={10}>
            <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.launchEmails.button" />
          </Button>
        </Flex>
        <Flex justifyContent="flex-end" marginTop>
          {error && <ErrorText>{error}</ErrorText>}
        </Flex>
      </GenericModal>
      <AbandonModal show={showAbandonModal} onClose={handleOnClose} onReturn={handleOnReturn} />
    </>
  )
}

export default injectIntl(LaunchInvitationsModal)
